import {
  languageSchema,
  latLngSchema,
  objectIdSchema,
  paginationWithSearchSchema,
  Region,
  RegionSchema,
  regionStatusSchema,
  RegionType,
  regionTypeSchema,
} from '@hero/schema';
import { z } from 'zod';
import { API, RegionDetailsDataAPI } from '..';

const schema = RegionSchema.model;

export const regionCreateAPISchema = schema
  .pick({ type: true, boundaries: true, status: true })
  .extend({
    /** the lat & lng of the location you want to add
     *
     * this will be center location
     */
    location: latLngSchema,
  })
  .partial({ boundaries: true, status: true });
export type RegionCreateAPI = API<z.infer<typeof regionCreateAPISchema>, Region>;

export const regionGetAPISchema = regionCreateAPISchema.pick({
  location: true,
  type: true,
});
export type RegionGetAPI = API<z.infer<typeof regionGetAPISchema>, Region>;

//* get one by id
export const regionGetOneSchemaAPI = schema.pick({ _id: true });
export type RegionGetOneAPI = API<z.infer<typeof regionGetOneSchemaAPI>, Region>;

export const regionManyAPISchema = regionGetAPISchema
  .pick({
    location: true,
    type: true,
  })
  .extend(paginationWithSearchSchema.shape)
  .extend({
    /** Workflow-status filter — e.g. `needs_review` for the Super-Admin review queue. */
    status: regionStatusSchema.optional(),
    /** Several types at once (e.g. `['city', 'hub']`). Wins over `type` when both are sent. */
    types: z.array(regionTypeSchema).min(1).optional(),
  })
  .partial({ location: true, type: true });

export type RegionsManyAPI = API<z.infer<typeof regionManyAPISchema>, Region[]>;

export const regionDetailsAPISchema = regionGetAPISchema.pick({ location: true });
export type RegionDetailsAPI = API<z.infer<typeof regionDetailsAPISchema>, RegionDetailsDataAPI>;

export const regionManyByTypeAPISchema = z.object({
  /** the type you want to search for */
  type: z.array(regionTypeSchema),
});
export type RegionsManyByTypeAPI = API<z.infer<typeof regionManyByTypeAPISchema>, Region[]>;

//*
export type RegionCity = Pick<Region, '_id' | 'customCode' | 'displayName'>;
export type RegionGetAllCitiesAPI = API<undefined, RegionCity[]>;

//* update
export const regionUpdateAPISchema = regionCreateAPISchema
  .omit({ location: true, boundaries: true })
  .partial()
  .extend({ _id: objectIdSchema });
export type RegionUpdateAPI = API<z.infer<typeof regionUpdateAPISchema>, Region>;

// ─── Saudi master import (super-admin, multipart) ────────────────────────
/**
 * Multipart upload of the Saudi logistics master Excel.
 *
 * Body is multipart/form-data: a single `.xlsx` file under field `file`, plus an
 * optional `dryRun=true` flag. The schema validates the non-file part only;
 * NestJS's `FileInterceptor` handles the file.
 */
export const regionImportSaudiMasterAPISchema = z.object({
  /** When `true`, parses + geocodes + counts without writing anything to the DB. */
  dryRun: z.coerce.boolean().optional().default(false),
});

/** Per-sheet bookkeeping returned by the importer. */
export type RegionImportSheetSummary = {
  /** Total rows seen in the sheet. */
  read: number;
  /** Rows that created a brand-new region doc. */
  created: number;
  /** Rows that updated an existing region doc (matched on `{ type, customCode }`). */
  updated: number;
  /** Rows skipped intentionally (e.g. empty country row with no geocodable lat/lng). */
  skipped: number;
  /** Rows that failed (see `errors[]` for the per-row reason). */
  errors: number;
};

/** Sheet name keys used in the import summary. Mirrors the Excel workbook. */
export type RegionImportSheetKey =
  | 'countries'
  | 'provinces'
  | 'governorates'
  | 'cities'
  | 'hubs';

export type RegionImportSaudiMasterAPI = API<
  z.infer<typeof regionImportSaudiMasterAPISchema>,
  {
    /** Per-sheet counts. */
    summary: Record<RegionImportSheetKey, RegionImportSheetSummary>;
    /** Per-row errors (truncated to the first 200 to keep the response bounded). */
    errors: Array<{
      sheet: RegionImportSheetKey;
      row: number;
      customCode?: string;
      message: string;
    }>;
    /** Set when `dryRun=true` so the client can label the result accordingly. */
    dryRun: boolean;
    /** Total Google Geocoding calls actually executed (post-cache). */
    googleCalls: number;
    /** Wall-clock duration in milliseconds. */
    elapsedMs: number;
  }
>;

// ─── Search (AI / NLU resolution) ────────────────────────────────────────
/**
 * `GET /region/search?q=...&types[]=&withinRegionCustomCode=...&limit=`.
 *
 * Backed by Atlas `$search` with multi-language analyzers + synonyms. Used by
 * dispatcher pickers AND by LLM/NLU consumers parsing free text like
 * "2 trucks from Riyadh to جدة".
 */
export const regionSearchAPISchema = z.object({
  q: z
    .string()
    .trim()
    .min(1)
    .describe(
      'Free-text query. Accepts English / Arabic / Urdu / transliteration. Backend uses Atlas multi-language analyzers + synonyms.',
    ),
  types: z.preprocess(
    // Query-string arrays arrive as a plain string when only one value is
    // sent (`?types=city`) — same normalization as `customCodes` below.
    val => (Array.isArray(val) ? val : val != null && val !== '' ? [val] : undefined),
    z
      .array(regionTypeSchema)
      .optional()
      .describe(
        "Filter to specific region types — e.g. `['city', 'hub']` for a route planner, `['country']` to disambiguate. Omit for any type.",
      ),
  ),
  withinRegionCustomCode: z
    .string()
    .trim()
    .toLowerCase()
    .optional()
    .describe(
      "Scope the search to descendants of this region's polygon (resolved at query time by `$geoIntersects`). Pass a country / province `customCode` to narrow.",
    ),
  limit: z.coerce
    .number()
    .min(1)
    .max(50)
    .default(10)
    .describe('Hard cap on result count (1..50). Defaults to 10.'),
});

/** Lightweight ancestor chain element returned alongside each hit. */
export type RegionSearchAncestor = Pick<
  Region,
  'type' | 'customCode' | 'displayName' | 'displayNameAr'
>;

/** One search hit. The ancestor chain is hydrated by the parent resolver. */
export type RegionSearchHit = Pick<
  Region,
  '_id' | 'type' | 'customCode' | 'displayName' | 'displayNameAr' | 'displayNameUr' | 'names' | 'center'
> & {
  /** Atlas Search relevance score. */
  score: number;
  /** Country -> ... chain that places this region in context. */
  ancestors: RegionSearchAncestor[];
  /**
   * Spelling / colloquial variants per language — lets a caller tell
   * whether the query actually named this region («الشرقية» names
   * `eastern`) or Atlas only matched it through keywords.
   */
  aliases?: Region['aliases'];
};

export type RegionSearchAPI = API<z.infer<typeof regionSearchAPISchema>, RegionSearchHit[]>;

// ─── Translate (render-time language resolution) ─────────────────────────
/**
 * `GET /region/translate?customCodes[]=riyadh&customCodes[]=jeddah&lang=ur`.
 *
 * Batch lookup of region display names in the viewer's language. Public — drivers,
 * dispatchers, and anonymous tracking pages all hit it. Cached aggressively
 * (`Cache-Control: public, max-age=86400` + ETag derived from the last importer run).
 *
 * See `docs/regions/01-translation-and-cache.md` for the 5-layer cache strategy.
 */
export const regionTranslateAPISchema = z.object({
  /** customCodes to resolve. Batched up to 100 per call (1 call per screen is the design target). */
  customCodes: z.preprocess(
    val => (Array.isArray(val) ? val : val != null && val !== '' ? [val] : []),
    z
      .array(z.string().trim().toLowerCase())
      .min(1)
      .max(100),
  ),
  /** Viewer's language. Defaults to `'en'` if the requested-lang display name is missing on the doc. */
  lang: languageSchema.default('en'),
});

/** One translated region entry, keyed by `customCode` in the response map. */
export type RegionTranslateEntry = {
  /** Display name in the requested language (or English fallback). */
  displayName: string;
  /** Region kind — useful for the UI to render an icon / label. */
  type: RegionType;
  /** Echoed back for convenience when iterating the response. */
  customCode: string;
  /** Set when the requested `lang` was empty on the doc and we fell back to another. */
  fallbackUsed?: z.infer<typeof languageSchema>;
  /** Set when `customCode` was not found at all — UI shows the raw code as-is. */
  notFound?: true;
};

export type RegionTranslateAPI = API<
  z.infer<typeof regionTranslateAPISchema>,
  Record<string, RegionTranslateEntry>
>;
